import { useEffect } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { PROVEDORES_OPENCODE } from '@remoto/protocolo';
import { rotaPainel } from './painel';
import { usarSessao } from '../estado/sessao';
import { socketDoPainel } from '../socket/painel';
import { SeloForaDeCasa } from '../componentes/SeloForaDeCasa';
import { Tela } from '../workspace/Tela';
import { mensagemDoErro } from '../erros';
import { CHAVE_DA_CONFIG, FRASES_DA_TELA, usarEstadoDaConfig } from '../configuracoes/dados';
import { SecaoDaChave } from '../configuracoes/SecaoDaChave';
import { SecaoDeModelos } from '../configuracoes/SecaoDeModelos';
import { SecaoDeMaquinas } from '../configuracoes/SecaoDeMaquinas';

/**
 * A tela de Configurações (F17, D17): hoje, só o OpenCode — o provedor dos
 * modelos e a chave de cada um (F2: o NanoGPT ativo, o OpenRouter guardado),
 * os modelos e o estado por máquina.
 *
 * Só ADMIN, e por três barreiras independentes: o item some do menu, o
 * `beforeLoad` devolve para Máquinas quem digitar o endereço, e a API recusa
 * de novo. As duas primeiras são conforto; a terceira é a que vale.
 */
function Configuracoes(): React.JSX.Element {
  const cliente = useQueryClient();
  const { data: estado, error, isLoading } = usarEstadoDaConfig();

  /*
    A presença das máquinas muda ao vivo: o agente que conecta ou cai mexe na
    coluna "no ar" e, logo depois, no retrato do OpenCode. É o mesmo evento que
    a tela de Máquinas escuta.
  */
  useEffect(() => {
    const socket = socketDoPainel();
    const aoMudar = (): void => {
      void cliente.invalidateQueries({ queryKey: CHAVE_DA_CONFIG });
    };
    socket.on('maquina.estado', aoMudar);
    return () => {
      socket.off('maquina.estado', aoMudar);
    };
  }, [cliente]);

  return (
    <Tela>
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 space-y-0.5">
          <h1 className="text-lg font-medium text-zinc-100">Configurações</h1>
          <p className="text-sm text-zinc-500">
            OpenCode, o terceiro motor: o provedor dos modelos e as chaves, os modelos e o estado
            em cada máquina.
          </p>
        </div>
        <SeloForaDeCasa provedor={estado?.config.provedor ?? null} />
      </header>

      {isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-red-900/60 bg-red-950/30 px-3 py-2 text-sm text-red-200"
        >
          {mensagemDoErro(error, 'Não foi possível carregar as configurações agora.', FRASES_DA_TELA)}
        </p>
      ) : null}

      {estado ? (
        <>
          {/*
            As duas chaves, a do provedor ATIVO primeiro: é a que a frota usa
            agora, e a outra fica guardada para o dia da troca.
          */}
          {[...PROVEDORES_OPENCODE]
            .sort((a, b) => Number(b === estado.config.provedor) - Number(a === estado.config.provedor))
            .map((provedor) => (
              <SecaoDaChave
                key={provedor}
                provedor={provedor}
                chave={estado.chaves[provedor]}
                provedorAtivo={estado.config.provedor}
              />
            ))}
          <SecaoDeModelos estado={estado} />
          <SecaoDeMaquinas estado={estado} />
        </>
      ) : null}
    </Tela>
  );
}

export const rotaConfiguracoes = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/admin/configuracoes',
  beforeLoad: async () => {
    // A API barra de novo; aqui é só para não abrir uma tela que vai falhar.
    if (usarSessao.getState().usuario?.papel !== 'ADMIN') throw redirect({ to: '/maquinas' });
  },
  component: Configuracoes,
});
